// Hook custom per recuperare i dettagli di un articolo specifico tramite API.
// Nota: uso axios e TypeScript per tipizzare la risposta, gestire errori, loading e refetch.

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ArticoloBlogDetail } from '../types/blog';

const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;

// Parametri dell'hook
interface UseArticoloByIdOptions {
    id: number | null | undefined;  // ID dell'articolo da recuperare
    agencyId: number;               // ID dell'agenzia
    checkWeb?: boolean;             // se controllare il flag web dell'articolo
    enabled?: boolean;              // se abilitare la chiamata (utile per componenti condizionali)
}

// Tipizzazione del risultato dell'hook
interface UseArticoloByIdResult {
    articolo: ArticoloBlogDetail | null;
    isLoading: boolean;
    error: string | null;
    refetch: () => void;  // funzione per richiamare manualmente la fetch
}

/**
 * useGetArticoloById
 * @param id - ID dell'articolo
 * @param agencyId - ID dell'agenzia
 * @param checkWeb - default true, controlla se l'articolo è pubblicabile sul web
 * @param enabled - default true, se false non esegue fetch
 *
 * Nota per il prossimo dev:
 * - `articolo` contiene i dettagli dell'articolo oppure null
 * - `isLoading` true durante la chiamata API
 * - `error` messaggio di errore in caso di fallimento
 * - `refetch()` permette di richiamare manualmente la fetch
 */
const useGetArticoloById = ({
    id,
    agencyId,
    checkWeb = true,
    enabled = true
}: UseArticoloByIdOptions): UseArticoloByIdResult => {

    const [articolo, setArticolo] = useState<ArticoloBlogDetail | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Funzione fetch interna con useCallback per evitare ricreazioni
    const fetchArticolo = useCallback(async () => {
        if (!enabled || typeof id !== 'number' || id <= 0) {
            if (typeof id !== 'number' || id <= 0) {
                setArticolo(null);
                setIsLoading(false);
                setError(null);
            }
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await axios.post(`${API_BASE_URL}/blog/articolo/detail`, {
                id,
                agencyId,
                checkWeb,
            });

            if (response.data && response.data.id) {
                setArticolo(response.data as ArticoloBlogDetail);
            } else {
                setArticolo(null);
                console.warn("API response successful but article data seems missing or invalid.");
            }

        } catch (err: unknown) {
            if (axios.isAxiosError(err)) {
                // err è tipizzato come AxiosError
                const errorMessage = err.response?.data?.error || err.message;
                setError(errorMessage);
            } else if (err instanceof Error) {
                // err è un normale Error di JS
                setError(err.message);
            } else {
                // fallback generico
                setError("Errore sconosciuto");
            }
        } finally {
            setIsLoading(false);
        }
    }, [id, agencyId, checkWeb, enabled]);

    // Effetto che richiama fetchArticolo quando cambia fetchArticolo (dipendenze)
    useEffect(() => {
        fetchArticolo();
    }, [fetchArticolo]);

    return {
        articolo,
        isLoading,
        error,
        refetch: fetchArticolo
    };
};

export default useGetArticoloById;